import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { kathaApi } from '../../api/api';
import { getCategoryMeta } from '../../data/kathaData';
import { youtubeThumb } from '../../utils/youtube';
import { AdminSelect, AdminFieldInput, AdminFieldTextarea, AdminFieldSelect, AdminModalClose } from '../components/AdminUI';

const CATEGORIES = ['katha', 'pravachan', 'bhajan', 'live', 'shorts', 'meditation'];
const CATEGORY_OPTIONS = CATEGORIES.map((c) => ({
  value: c,
  label: c.charAt(0).toUpperCase() + c.slice(1),
}));
const PAGE_SIZE_OPTIONS = [6, 12, 18, 24];

const EMPTY_FORM = {
  title: '', youtubeId: '', playlistUrl: '',
  category: 'katha', description: '', thumbnail: '',
  type: 'video', views: '', likes: '', playlistViews: '', duration: '', sortOrder: 0,
};

const TITLE_HOVER = '#ea580c';
const BTN_GRADIENT = 'linear-gradient(to right, #f97316, #ea580c)';

const clampStyle = (lines) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

const Input = ({ label, required, clearable, onClear, ...props }) => (
  <AdminFieldInput label={label} required={required} clearable={clearable} onClear={onClear} {...props} />
);

const pad2 = (n) => String(Math.max(0, parseInt(n, 10) || 0)).padStart(2, '0');

/** Parse "45:30" | "1:15:20" → { h, m, s } */
const parseDuration = (str) => {
  const parts = String(str || '').split(':').map((p) => parseInt(p, 10) || 0);
  if (parts.length === 3) return { h: parts[0], m: parts[1], s: parts[2] };
  if (parts.length === 2) return { h: 0, m: parts[0], s: parts[1] };
  if (parts.length === 1 && parts[0]) return { h: 0, m: parts[0], s: 0 };
  return { h: 0, m: 0, s: 0 };
};

const formatDuration = (h, m, s) => {
  const hh = parseInt(h, 10) || 0;
  const mm = Math.min(59, parseInt(m, 10) || 0);
  const ss = Math.min(59, parseInt(s, 10) || 0);
  if (hh > 0) return `${hh}:${pad2(mm)}:${pad2(ss)}`;
  return `${mm}:${pad2(ss)}`;
};

/** Duration picker: Hours / Min / Sec — select-all on focus so typing replaces 0 */
const DurationPicker = ({ label, value, onChange }) => {
  const parsed = parseDuration(value);
  const [focusKey, setFocusKey] = useState(null);
  const [draft, setDraft] = useState('');

  const commit = (key, raw) => {
    const n = raw === '' ? 0 : Math.max(0, parseInt(String(raw).replace(/^0+(?=\d)/, ''), 10) || 0);
    const next = {
      h: key === 'h' ? Math.min(99, n) : parsed.h,
      m: key === 'm' ? Math.min(59, n) : parsed.m,
      s: key === 's' ? Math.min(59, n) : parsed.s,
    };
    onChange(formatDuration(next.h, next.m, next.s));
  };

  const display = (key) => (focusKey === key ? draft : String(parsed[key]));

  const fieldCls = 'admin-field-input text-center';

  const renderField = (k, sub) => (
    <div key={k}>
      <input
        type="search"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        value={display(k)}
        className={fieldCls}
        onFocus={(e) => {
          setFocusKey(k);
          setDraft(String(parsed[k]));
          requestAnimationFrame(() => e.target.select());
        }}
        onBlur={() => {
          commit(k, draft);
          setFocusKey(null);
        }}
        onChange={(e) => {
          // Digits only; strip leading zeros so "05" → "5"
          const cleaned = e.target.value.replace(/\D/g, '').slice(0, 2).replace(/^0+(?=\d)/, '');
          setDraft(cleaned);
          commit(k, cleaned === '' ? '0' : cleaned);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.target.blur();
        }}
      />
      <p className="text-center text-xs text-gray-400 mt-0.5">{sub}</p>
    </div>
  );

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
      <div className="grid grid-cols-3 gap-2">
        {renderField('h', 'Hrs')}
        {renderField('m', 'Min')}
        {renderField('s', 'Sec')}
      </div>
    </div>
  );
};

/** Views: number + K/M unit (stores e.g. "25K"); default unit K */
const ViewsInput = ({ label, value, onChange }) => {
  const raw = String(value || '');
  const match = raw.match(/^(\d+(?:\.\d+)?)\s*([KkMm]?)$/);
  const num = match ? match[1] : (raw.replace(/[^\d.]/g, '') || '');
  // Default to K when empty / no unit yet
  const unit = match && match[2] ? match[2].toUpperCase() : (raw ? '' : 'K');
  const emit = (n, u) => {
    if (n === '' || n == null) { onChange(''); return; }
    onChange(u ? `${n}${u}` : String(n));
  };
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
      <div className="flex gap-2">
        <input
          type="search"
          inputMode="numeric"
          min={0}
          step="any"
          autoComplete="off"
          value={num}
          placeholder="e.g. 25"
          onChange={(e) => emit(e.target.value, unit || 'K')}
          className="admin-field-input flex-1"
        />
        <select
          value={unit || 'K'}
          onChange={(e) => emit(num, e.target.value)}
          className="admin-field-select"
          style={{ width: '5rem', flexShrink: 0 }}
        >
          <option value="K">K</option>
          <option value="M">M</option>
          <option value="">—</option>
        </select>
      </div>
    </div>
  );
};

/** Website-style trending card + Edit / Delete */
const KathaAdminCard = ({ item, onEdit, onDelete }) => {
  const meta = getCategoryMeta(item.category);
  const thumb = item.thumbnail || youtubeThumb(item.youtubeId) || '/logo.jpg';
  const isPlaylist = item.type === 'playlist';
  const bottomText = isPlaylist
    ? (item.views ? `${item.views} videos` : 'Playlist')
    : (item.duration || 'Video');
  const watchUrl = isPlaylist
    ? item.playlistUrl
    : (item.youtubeId ? `https://www.youtube.com/watch?v=${item.youtubeId}` : null);

  const openWatch = () => {
    if (watchUrl) window.open(watchUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      role={watchUrl ? 'link' : undefined}
      tabIndex={watchUrl ? 0 : undefined}
      onClick={openWatch}
      onKeyDown={(e) => {
        if (watchUrl && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          openWatch();
        }
      }}
      className={`group flex flex-col border rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden bg-white ${watchUrl ? 'cursor-pointer' : ''}`}
      style={{ borderColor: '#e5e7eb' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#fdba74';
        const titleEl = e.currentTarget.querySelector('h3');
        if (titleEl) titleEl.style.color = TITLE_HOVER;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#e5e7eb';
        const titleEl = e.currentTarget.querySelector('h3');
        if (titleEl) titleEl.style.color = '';
      }}
    >
      <div className="relative overflow-hidden">
        <img
          src={thumb}
          alt={item.title}
          className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-300 pointer-events-none"
          onError={(e) => { e.target.src = '/logo.jpg'; }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0.1), transparent)' }}></div>

        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none">
          <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full w-14 h-14 flex items-center justify-center">
            <div className="rounded-full w-10 h-10 flex items-center justify-center shadow-xl" style={{ background: BTN_GRADIENT }}>
              <i className={`fas ${isPlaylist ? 'fa-list' : 'fa-play'} text-white text-sm ${isPlaylist ? '' : 'ml-0.5'}`}></i>
            </div>
          </div>
        </div>

        <div className="absolute top-2 left-2 right-2 flex justify-between items-start pointer-events-none">
          <div className="bg-black bg-opacity-60 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium">
            <i className={`${meta.icon} mr-1`}></i>{meta.name}
          </div>
          <span
            className="px-2 py-1 rounded-full text-xs font-semibold text-white"
            style={{ background: isPlaylist ? '#8b5cf6' : '#ef4444' }}
          >
            {isPlaylist ? 'Playlist' : 'Video'}
          </span>
        </div>

        <div className="absolute bottom-2 left-2 pointer-events-none">
          <div className="bg-black bg-opacity-60 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium">
            <i className={`fas ${isPlaylist ? 'fa-list' : 'fa-clock'} mr-1`}></i>{bottomText}
          </div>
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-semibold text-base text-gray-800 mb-2 transition-colors leading-tight" style={clampStyle(2)}>
          {item.title}
        </h3>
        <div className="flex items-center justify-between mb-3 text-xs text-gray-600">
          <div className="flex items-center">
            <i className="fas fa-eye mr-1" style={{ color: '#f97316' }}></i>
            <span>{isPlaylist
              ? (item.views ? `${item.views} videos` : 'Playlist')
              : (item.views ? `${item.views} views` : 'Video')}</span>
          </div>
          {item.youtubeId && (
            <div className="flex items-center truncate ml-2">
              <i className="fab fa-youtube mr-1" style={{ color: '#ef4444' }}></i>
              <span className="truncate">{item.youtubeId}</span>
            </div>
          )}
        </div>
        {item.description ? (
          <p className="text-gray-600 text-xs mb-3 leading-relaxed" style={clampStyle(2)}>{item.description}</p>
        ) : (
          <p className="text-gray-400 text-xs mb-3 italic">No description</p>
        )}

        <div className="mt-auto flex gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={onEdit}
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold"
            style={{ background: '#eff6ff', color: '#2563eb' }}
          >
            <i className="fas fa-edit"></i> Edit
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold"
            style={{ background: '#fef2f2', color: '#dc2626' }}
          >
            <i className="fas fa-trash"></i> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const KathaModal = ({ mode, initial, onSave, onClose, saving }) => {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [thumbTab, setThumbTab] = useState('auto'); // 'auto' | 'url' | 'upload'
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState('');
  const fileInputRef = useRef(null);

  const set = (f, v) => { setForm((p) => ({ ...p, [f]: v })); setFormError(''); };

  useEffect(() => {
    setForm(initial || EMPTY_FORM);
    setSelectedFile(null);
    setFilePreview('');
    // Decide initial tab: if thumbnail is set and not a YT auto-url, show 'url' tab
    const t = (initial || EMPTY_FORM).thumbnail || '';
    const ytAutoPattern = /img\.youtube\.com\/vi\//;
    setThumbTab(t && !ytAutoPattern.test(t) ? 'url' : 'auto');
  }, [initial]);

  const switchThumbTab = (tab) => {
    setThumbTab(tab);
    setFormError('');
    if (tab === 'auto') { set('thumbnail', ''); setSelectedFile(null); setFilePreview(''); }
    if (tab === 'url') { setSelectedFile(null); setFilePreview(''); }
    if (tab === 'upload') { set('thumbnail', ''); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setFilePreview(URL.createObjectURL(file));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setFormError('Title is required'); return; }
    if (form.type === 'video' && !form.youtubeId.trim()) { setFormError('YouTube Video ID is required'); return; }
    if (form.type === 'playlist' && !form.playlistUrl.trim()) { setFormError('Playlist URL is required'); return; }
    if (thumbTab === 'url' && !form.thumbnail.trim()) { setFormError('Thumbnail URL is required'); return; }
    if (thumbTab === 'upload' && !selectedFile && !form.thumbnail.trim()) { setFormError('Please select a thumbnail image'); return; }

    if (thumbTab === 'upload' && selectedFile) {
      const fd = new FormData();
      fd.append('image', selectedFile);
      fd.append('title', form.title);
      fd.append('youtubeId', form.youtubeId || '');
      fd.append('playlistUrl', form.playlistUrl || '');
      fd.append('category', form.category);
      fd.append('description', form.description || '');
      fd.append('type', form.type);
      fd.append('views', form.views || '');
      fd.append('likes', form.likes || '');
      fd.append('playlistViews', form.playlistViews || '');
      fd.append('duration', form.duration || '');
      fd.append('sortOrder', parseInt(form.sortOrder, 10) || 0);
      onSave(fd, true);
    } else {
      onSave({ ...form, sortOrder: parseInt(form.sortOrder, 10) || 0 }, false);
    }
  };

  // Compute live thumbnail preview
  const autoThumb = youtubeThumb(form.youtubeId);
  const thumbPreview = thumbTab === 'upload'
    ? (filePreview || form.thumbnail || '')
    : thumbTab === 'url'
      ? form.thumbnail || ''
      : autoThumb;

  const tabBtn = (tab, icon, label) => (
    <button
      type="button"
      onClick={() => switchThumbTab(tab)}
      className={`flex-1 py-2 text-center transition-colors text-sm font-medium ${thumbTab === tab ? 'admin-chip-active' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
    >
      <i className={`fas ${icon} mr-1`}></i>{label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-screen overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-800 text-lg">
            {mode === 'edit' ? 'Edit Katha Entry' : 'Add Katha Entry'}
          </h3>
          <AdminModalClose onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit} autoComplete="off" className="p-6 space-y-4">
          {formError && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              <i className="fas fa-exclamation-circle mr-2"></i>{formError}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <AdminFieldSelect
              label="Type"
              value={form.type}
              onChange={(v) => set('type', v)}
              options={[
                { value: 'video', label: 'Video' },
                { value: 'playlist', label: 'Playlist' },
              ]}
              aria-label="Type"
            />
            <AdminFieldSelect
              label="Category"
              value={form.category}
              onChange={(v) => set('category', v)}
              options={CATEGORY_OPTIONS}
              aria-label="Category"
            />
          </div>

          <Input
            label="Title"
            required
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
            onClear={() => set('title', '')}
            clearable
            placeholder="e.g. Divine Birth of Lord Krishna"
          />

          {form.type === 'video' ? (
            <div className="space-y-3">
              <Input
                label="YouTube Video ID"
                required
                value={form.youtubeId || ''}
                onChange={(e) => set('youtubeId', e.target.value)}
                onClear={() => set('youtubeId', '')}
                clearable
                placeholder="e.g. AGjx27_87iY"
              />
              <div className="grid grid-cols-2 gap-3">
                <DurationPicker label="Duration" value={form.duration || ''} onChange={(v) => set('duration', v)} />
                <ViewsInput label="Views" value={form.views || ''} onChange={(v) => set('views', v)} />
              </div>
              <ViewsInput label="Likes" value={form.likes || ''} onChange={(v) => set('likes', v)} />
            </div>
          ) : (
            <div className="space-y-3">
              <Input
                label="YouTube Playlist URL"
                required
                value={form.playlistUrl || ''}
                onChange={(e) => set('playlistUrl', e.target.value)}
                onClear={() => set('playlistUrl', '')}
                clearable
                placeholder="https://www.youtube.com/playlist?list=..."
              />
              <Input
                label="Cover Video ID (for thumbnail)"
                value={form.youtubeId || ''}
                onChange={(e) => set('youtubeId', e.target.value)}
                onClear={() => set('youtubeId', '')}
                clearable
                placeholder="e.g. AGjx27_87iY — used to auto-generate thumbnail"
              />
              <p className="text-xs text-gray-400 -mt-2 pl-0.5">
                <i className="fas fa-info-circle mr-1 text-orange-400"></i>
                Paste any video ID from this playlist — its thumbnail will be used automatically.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {/* Video Count — stepper */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Total Videos</label>
                  <div className="flex items-center gap-0 rounded-xl border border-gray-200 overflow-hidden" style={{ height: '44px' }}>
                    <button
                      type="button"
                      onClick={() => set('views', String(Math.max(0, (parseInt(form.views, 10) || 0) - 1)))}
                      className="flex items-center justify-center text-gray-500 hover:text-white hover:bg-orange-500 transition-all duration-150 font-bold text-lg select-none"
                      style={{ width: '36px', minWidth: '36px', height: '44px', borderRight: '1px solid #e5e7eb' }}
                      tabIndex={-1}
                    >−</button>
                    <input
                      type="search"
                      inputMode="numeric"
                      autoComplete="off"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      value={String(parseInt(form.views, 10) || 0)}
                      placeholder="0"
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9]/g, '');
                        set('views', raw === '' ? '0' : raw);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowUp') { e.preventDefault(); set('views', String((parseInt(form.views, 10) || 0) + 1)); }
                        if (e.key === 'ArrowDown') { e.preventDefault(); set('views', String(Math.max(0, (parseInt(form.views, 10) || 0) - 1))); }
                      }}
                      className="flex-1 text-center text-sm font-semibold text-gray-800 bg-white outline-none border-none"
                      style={{ height: '44px' }}
                    />
                    <button
                      type="button"
                      onClick={() => set('views', String((parseInt(form.views, 10) || 0) + 1))}
                      className="flex items-center justify-center text-gray-500 hover:text-white hover:bg-orange-500 transition-all duration-150 font-bold text-lg select-none"
                      style={{ width: '36px', minWidth: '36px', height: '44px', borderLeft: '1px solid #e5e7eb' }}
                      tabIndex={-1}
                    >+</button>
                  </div>
                </div>
                {/* Playlist total views */}
                <ViewsInput label="Playlist Views" value={form.playlistViews || ''} onChange={(v) => set('playlistViews', v)} />
              </div>
            </div>
          )}

          {/* Thumbnail Section — 3-tab picker */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Thumbnail</label>
            <div className="flex rounded-xl border border-gray-200 overflow-hidden">
              {tabBtn('auto', 'fa-youtube', form.type === 'video' ? 'Auto (YouTube)' : 'Auto (Cover ID)')}
              {tabBtn('url', 'fa-link', 'URL')}
              {tabBtn('upload', 'fa-upload', 'Upload')}
            </div>
          </div>

          {thumbTab === 'url' && (
            <Input
              label="Thumbnail URL"
              required
              value={form.thumbnail || ''}
              onChange={(e) => set('thumbnail', e.target.value)}
              onClear={() => set('thumbnail', '')}
              clearable
              placeholder="https://..."
            />
          )}

          {thumbTab === 'upload' && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Thumbnail Image <span className="text-red-500">*</span></label>
              <div
                onClick={() => fileInputRef.current.click()}
                className="border-2 border-dashed border-gray-300 rounded-xl p-5 text-center cursor-pointer hover:border-orange-400 transition-colors bg-gray-50"
              >
                {selectedFile ? (
                  <p className="text-sm text-green-600 font-medium"><i className="fas fa-check-circle mr-2"></i>{selectedFile.name}</p>
                ) : (
                  <>
                    <i className="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"></i>
                    <p className="text-sm text-gray-500">Click to select image</p>
                    <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP up to 10 MB</p>
                  </>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
            </div>
          )}

          {thumbTab === 'auto' && (
            <p className="text-xs text-gray-400 pl-0.5">
              <i className="fas fa-info-circle mr-1 text-orange-400"></i>
              {form.type === 'video'
                ? 'Thumbnail will be fetched automatically from YouTube using the Video ID above.'
                : 'Thumbnail will be fetched from YouTube using the Cover Video ID above.'}
            </p>
          )}

          {thumbPreview && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Thumbnail Preview</label>
              <img
                src={thumbPreview}
                alt="thumbnail"
                className="w-full aspect-video object-cover rounded-xl border border-gray-200"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}

          <AdminFieldTextarea
            label="Description"
            rows={2}
            value={form.description || ''}
            onChange={(e) => set('description', e.target.value)}
            placeholder="Brief description..."
          />

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Sort Order</label>
            <div className="flex items-center gap-0 rounded-xl border border-gray-200 overflow-hidden" style={{ height: '44px' }}>
              <button
                type="button"
                onClick={() => set('sortOrder', Math.max(0, (parseInt(form.sortOrder, 10) || 0) - 1))}
                className="flex items-center justify-center text-gray-500 hover:text-white hover:bg-orange-500 transition-all duration-150 font-bold text-lg select-none"
                style={{ width: '44px', minWidth: '44px', height: '44px', borderRight: '1px solid #e5e7eb' }}
                tabIndex={-1}
              >−</button>
              <input
                type="search"
                inputMode="numeric"
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={String(parseInt(form.sortOrder, 10) || 0)}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  set('sortOrder', raw === '' ? 0 : parseInt(raw, 10));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp') { e.preventDefault(); set('sortOrder', (parseInt(form.sortOrder, 10) || 0) + 1); }
                  if (e.key === 'ArrowDown') { e.preventDefault(); set('sortOrder', Math.max(0, (parseInt(form.sortOrder, 10) || 0) - 1)); }
                }}
                className="flex-1 text-center text-base font-semibold text-gray-800 bg-white outline-none border-none"
                style={{ height: '44px' }}
              />
              <button
                type="button"
                onClick={() => set('sortOrder', (parseInt(form.sortOrder, 10) || 0) + 1)}
                className="flex items-center justify-center text-gray-500 hover:text-white hover:bg-orange-500 transition-all duration-150 font-bold text-lg select-none"
                style={{ width: '44px', minWidth: '44px', height: '44px', borderLeft: '1px solid #e5e7eb' }}
                tabIndex={-1}
              >+</button>
            </div>
            <p className="text-xs text-gray-400 mt-1 pl-0.5">
              <i className="fas fa-info-circle mr-1 text-orange-400"></i>
              <strong>0</strong> = newest first (default). Set <strong>1, 2, 3…</strong> to manually pin this item to the top.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 px-4 border border-gray-200 rounded-xl text-gray-600 font-semibold text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 px-4 admin-btn-primary text-sm disabled:opacity-60">
              {saving ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : <><i className="fas fa-save"></i> Save</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ConfirmDialog = ({ title, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={onCancel}>
    <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
      <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: '#fee2e2' }}>
        <i className="fas fa-trash text-xl" style={{ color: '#ef4444' }}></i>
      </div>
      <h4 className="font-bold text-gray-800 mb-1">Delete Entry?</h4>
      <p className="text-sm text-gray-500 mb-4">"{title}" will be permanently removed.</p>
      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 py-2 border border-gray-200 rounded-xl text-gray-600 font-semibold text-sm">Cancel</button>
        <button onClick={onConfirm} disabled={loading}
          className="flex-1 py-2 admin-btn-danger rounded-xl font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2">
          {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-trash"></i>} Delete
        </button>
      </div>
    </div>
  </div>
);

const KathaAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(6);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await kathaApi.getAll(typeFilter ? { type: typeFilter } : {});
      if (res.success) setItems(res.data);
      else setError(res.message);
    } catch { setError('Failed to load katha entries'); }
    finally { setLoading(false); }
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setCurrentPage(1); }, [search, typeFilter, perPage]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) =>
      (item.title || '').toLowerCase().includes(q) ||
      (item.category || '').toLowerCase().includes(q) ||
      (item.youtubeId || '').toLowerCase().includes(q) ||
      (item.description || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / perPage));
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredItems.slice(start, start + perPage);
  }, [filteredItems, currentPage, perPage]);

  const rangeStart = filteredItems.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const rangeEnd = Math.min(currentPage * perPage, filteredItems.length);

  const handleSave = async (form, isFormData) => {
    setSaving(true);
    try {
      let res;
      if (modal.mode === 'edit') {
        res = isFormData
          ? await kathaApi.updateFormData(modal.item.id, form)
          : await kathaApi.update(modal.item.id, form);
      } else {
        res = isFormData
          ? await kathaApi.createFormData(form)
          : await kathaApi.create(form);
      }
      if (!res.success) throw new Error(res.message);
      setModal(null);
      showToast(modal.mode === 'edit' ? '✅ Updated!' : '✅ Added!');
      load();
    } catch (err) { alert(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await kathaApi.delete(deleteTarget.id);
      if (!res.success) throw new Error(res.message);
      setDeleteTarget(null);
      showToast('🗑️ Deleted');
      load();
    } catch (err) { alert(err.message || 'Delete failed'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {toast && <div className="fixed bottom-6 right-6 z-50 bg-gray-800 text-white px-5 py-3 rounded-2xl shadow-2xl text-sm font-medium">{toast}</div>}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">🔥 Katha Videos & Playlists</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? 'Loading…' : `${filteredItems.length} total · showing ${rangeStart}–${rangeEnd}`}
          </p>
        </div>
        <button onClick={() => setModal({ mode: 'add', item: { ...EMPTY_FORM } })} className="admin-btn-primary px-5 py-2.5 text-sm">
          <i className="fas fa-plus"></i> Add Entry
        </button>
      </div>

      {/* Filters — same bar as Events admin */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 mb-5">
        <div className="flex flex-col md:flex-row gap-2 md:items-center">
          <div className="admin-search-wrap flex-1 min-w-0">
            <i className="fas fa-search admin-search-icon" aria-hidden="true"></i>
            <input
              type="search"
              inputMode="search"
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title, category or YouTube ID..."
              className="admin-search-input"
            />
            {search && (
              <button
                type="button"
                className="admin-search-clear"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            )}
          </div>
          <AdminSelect
            aria-label="Filter by type"
            value={typeFilter}
            onChange={setTypeFilter}
            options={[
              { value: '', label: 'All Types' },
              { value: 'video', label: 'Videos' },
              { value: 'playlist', label: 'Playlists' },
            ]}
          />
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-gray-500 whitespace-nowrap">Per page</span>
            <AdminSelect
              aria-label="Items per page"
              value={perPage}
              onChange={setPerPage}
              options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <i className="fas fa-exclamation-circle mr-2"></i>{error}
          <button onClick={load} className="ml-3 underline">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center">
          <i className="fas fa-spinner fa-spin text-2xl" style={{ color: '#f97316' }}></i>
          <p className="text-gray-500 text-sm mt-3">Loading...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="py-20 text-center bg-white rounded-2xl border border-dashed border-gray-200">
          <i className="fas fa-video text-4xl mb-3" style={{ color: '#d1d5db' }}></i>
          <p className="text-gray-500 font-medium">No katha entries found</p>
          <button onClick={() => setModal({ mode: 'add', item: { ...EMPTY_FORM } })} className="mt-4 admin-btn-primary px-5 py-2.5 text-sm">
            <i className="fas fa-plus mr-2"></i>Add Entry
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {paginatedItems.map((item) => (
              <KathaAdminCard
                key={item.id}
                item={item}
                onEdit={() => setModal({ mode: 'edit', item })}
                onDelete={() => setDeleteTarget(item)}
              />
            ))}
          </div>

          {/* Pagination — same as Events admin */}
          <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs text-gray-500 leading-none">
              Page {currentPage} of {totalPages} · {filteredItems.length} entr{filteredItems.length !== 1 ? 'ies' : 'y'}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${currentPage === 1 ? 'opacity-40 cursor-not-allowed bg-gray-100 text-gray-400' : 'bg-white border border-gray-200 text-gray-700 hover:border-orange-400'}`}
              >
                <i className="fas fa-chevron-left mr-1"></i>Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => totalPages <= 7 || p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) => (
                  p === '…' ? (
                    <span key={`e${idx}`} className="px-1 text-gray-400 text-sm">…</span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setCurrentPage(p)}
                      className="w-9 h-9 rounded-lg text-sm font-semibold"
                      style={p === currentPage
                        ? { background: 'linear-gradient(to right, #f97316, #dc2626)', color: '#fff' }
                        : { background: '#fff', color: '#4b5563', border: '1px solid #e5e7eb' }}
                    >
                      {p}
                    </button>
                  )
                ))}
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${currentPage === totalPages ? 'opacity-40 cursor-not-allowed bg-gray-100 text-gray-400' : 'bg-white border border-gray-200 text-gray-700 hover:border-orange-400'}`}
              >
                Next<i className="fas fa-chevron-right ml-1"></i>
              </button>
            </div>
          </div>
        </>
      )}

      {modal && (
        <KathaModal
          mode={modal.mode}
          initial={modal.item ? {
            title: modal.item.title || '',
            youtubeId: modal.item.youtubeId || '',
            playlistUrl: modal.item.playlistUrl || '',
            category: modal.item.category || 'katha',
            description: modal.item.description || '',
            thumbnail: modal.item.thumbnail || '',
            type: modal.item.type || 'video',
            views: modal.item.views || '',
            likes: modal.item.likes || '',
            playlistViews: modal.item.playlistViews || '',
            duration: modal.item.duration || '',
            sortOrder: modal.item.sortOrder || 0,
          } : EMPTY_FORM}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}
      {deleteTarget && (
        <ConfirmDialog
          title={deleteTarget.title}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
    </div>
  );
};

export default KathaAdmin;
